<!--
  TimeInput — the form input for time fields, on a record page.

  A one-time field is an input showing its date ("Fri, Jan 8, 2027 · in 3
  months"); focusing it drops TimePicker below, with the same propose-then-Set
  step as the grid. A field of many is TimesEditor laid out in place: open a
  line to edit it, × to remove, type to add; Done writes, Cancel resets. Both
  write through core — an edit updates its row in place — then emit `changed`
  so the parent refetches. Read-only (or tracked) fields list their times.

  With no record (the filter builder) it is a plain date input whose value is
  the day.
-->
<template>
  <div class="time-input" :class="`is-${size}`">
    <input
      v-if="!item"
      class="input"
      :class="`is-${size}`"
      type="date"
      :value="typeof modelValue === 'string' ? modelValue.slice(0, 10) : ''"
      :disabled="readonly"
      @change="emit('update:modelValue', ($event.target as HTMLInputElement).value || null)"
    />

    <template v-else-if="!editable">
      <TimeElement
        v-for="row in rows"
        :key="row.id"
        :value="row"
        :show-kind="kind == null"
        :single="!field?.isArray"
        detail
      />
      <span v-if="!rows.length" class="empty">—</span>
    </template>

    <template v-else-if="!field?.isArray">
      <button
        ref="oneRef"
        type="button"
        class="input one"
        :class="{ 'is-open': open }"
        @click="open = !open"
      >
        <font-awesome-icon icon="calendar" class="icon" />
        <template v-if="rows[0]">
          <span>{{ oneText }}</span>
          <span class="weak">{{ oneDetail }}</span>
        </template>
        <span v-else class="weak">Add a date</span>
      </button>
      <Teleport to="body">
        <div v-if="open" ref="panelRef" class="time-input-panel" :style="floatingStyles">
          <TimePicker
            :value="rows[0] ?? null"
            :kind="kind ?? 'instant'"
            :label="field?.label"
            @set="setOne"
            @clear="write({ remove: [{ id: rows[0]!.id! }] })"
            @cancel="open = false"
          />
        </div>
      </Teleport>
    </template>

    <TimesEditor
      v-else
      :key="editorKey"
      class="many"
      :rows="rows"
      :kind="kind"
      :autofocus="false"
      footer-when-dirty
      @done="write"
      @cancel="reset"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from "vue"
import { toast } from "vue-sonner"
import { autoUpdate, flip, offset, shift, useFloating } from "@floating-ui/vue"
import type { AugerField } from "@auger/core/fields"
import TimeElement from "@auger/ui/components/elements/TimeElement.vue"
import {
  formatTime,
  latestFirst,
  longDay,
  type TimeKind,
  timeParts,
  type TimeRow,
} from "@auger/ui/utils/times"
import TimePicker from "@/components/times/TimePicker.vue"
import TimesEditor from "@/components/times/TimesEditor.vue"
import type { TimesDelta } from "@/composables/useTimesEditor"
import { client } from "@/init/apollo"
import { getAuger } from "@/init/auger"
import { errorMessage } from "@/utils/errors"

const props = withDefaults(
  defineProps<{
    modelValue?: unknown
    /** The record (or listing wrapping it) whose times these are. */
    item?: Record<string, unknown> | null
    field?: AugerField
    readonly?: boolean
    size?: string
  }>(),
  { modelValue: undefined, item: null, field: undefined, readonly: false, size: "normal" }
)

const emit = defineEmits<{
  /** The day, with no record (the filter builder). */
  "update:modelValue": [value: string | null]
  /** Written through core; the parent refetches. */
  changed: []
}>()

const rows = computed<TimeRow[]>(() => {
  const resolved = props.item && props.field ? props.field.value(props.item) : null
  const list = resolved == null ? [] : Array.isArray(resolved) ? resolved : [resolved]
  return latestFirst(
    list.filter((r): r is TimeRow => !!r && typeof r === "object" && "start_at" in r)
  )
})

const kind = computed<TimeKind | null>(() =>
  !props.field || props.field.type === "time" ? null : (props.field.type as TimeKind)
)
const recordId = computed(() => {
  const item = props.item as { id?: number; record?: { id?: number } } | null
  return item?.record?.id ?? item?.id ?? null
})
const editable = computed(
  () =>
    !props.readonly && !!props.field?.isEditable && !props.field.tracks && recordId.value != null
)

// --- One time -------------------------------------------------------------------

const open = ref(false)
const oneRef = ref<HTMLElement | null>(null)
const panelRef = ref<HTMLElement | null>(null)
// The picker floats on <body>, so a block that clips or scrolls can't cut it off.
const { floatingStyles } = useFloating(oneRef, panelRef, {
  placement: "bottom-start",
  middleware: [offset(4), flip(), shift({ padding: 8 })],
  whileElementsMounted: autoUpdate,
})

// A press outside the input and the picker closes it, setting nothing.
function onPressOutside(event: PointerEvent) {
  const target = event.target as Node
  if (oneRef.value?.contains(target) || panelRef.value?.contains(target)) return
  open.value = false
}
watch(open, (on) => {
  if (on) document.addEventListener("pointerdown", onPressOutside, true)
  else document.removeEventListener("pointerdown", onPressOutside, true)
})
onBeforeUnmount(() => document.removeEventListener("pointerdown", onPressOutside, true))
const oneText = computed(() => {
  const p = rows.value[0] ? timeParts(rows.value[0]) : null
  return p ? longDay(p.date, p.allDay ? null : p.time) : ""
})
const oneDetail = computed(() =>
  rows.value[0] ? formatTime(rows.value[0], { single: true }).detail.split(" · ")[1] : ""
)

function setOne(columns: {
  start_at: string
  end_at: string | null
  all_day: boolean
  timezone: string
}) {
  const row = rows.value[0]
  void write(
    row?.id
      ? { update: [{ id: row.id, ...columns }] }
      : {
          add: [
            {
              timeType: kind.value ?? "instant",
              startAt: columns.start_at,
              endAt: columns.end_at,
              allDay: columns.all_day,
              timezone: columns.timezone,
            },
          ],
        }
  )
}

// --- Many -----------------------------------------------------------------------

// Remounting the editor resets its copy to the stored rows.
const editorKey = ref(0)
function reset() {
  editorKey.value++
}

// --- Writing --------------------------------------------------------------------

async function write(delta: Partial<TimesDelta>) {
  const field = props.field
  if (!field || recordId.value == null) return
  try {
    await getAuger(client).records.update(
      recordId.value,
      { [field.sourceKey ?? field.key]: delta },
      { read: false }
    )
    open.value = false
    reset()
    emit("changed")
  } catch (err) {
    const error = errorMessage(err)
    toast.error(`Couldn't save ${field.label ?? "the time"}: ${error}`)
  }
}
</script>

<style>
.time-input {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;

  .one {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    text-align: left;
    cursor: pointer;

    &.is-open {
      border-color: var(--auger-primary);
    }
  }

  .icon,
  .weak,
  .empty {
    color: var(--auger-text-weak);
  }

  .many {
    padding: 4px 6px 6px;
    border: 1px solid var(--auger-border);
    border-radius: 6px;
  }
}

/* Teleported to <body>, so styled at the top level. */
.time-input-panel {
  z-index: 1000;
  padding: 10px;
  border: 1px solid var(--auger-border);
  border-radius: 6px;
  background: var(--auger-bg);
  font-size: var(--auger-size-6);
  box-shadow: var(--auger-shadow-3);
}
</style>
